import { ApolloClient, ApolloLink, HttpLink, InMemoryCache } from '@apollo/client'
import {
  CombinedGraphQLErrors,
  ServerError,
  ServerParseError,
} from '@apollo/client/errors'
import { ErrorLink } from '@apollo/client/link/error'
import { GraphQLWsLink } from '@apollo/client/link/subscriptions'
import { getMainDefinition } from '@apollo/client/utilities'
import { createClient } from 'graphql-ws'
import { toast } from 'react-toastify'
import { uploadLink, variablesHaveFiles } from 'src/apollo/upload-link'
import { BasicRoutesEnum } from 'src/routes/routes'

const AUTH_ROUTES: string[] = [BasicRoutesEnum.SIGN_IN, BasicRoutesEnum.SIGN_UP]

const PAGE_LIMIT = 20

interface PagedResult {
  results: unknown[]
}

const mergePaged = (
  existing: PagedResult | undefined,
  incoming: PagedResult,
  page: number,
  limit: number,
): PagedResult => {
  if (page <= 1) return incoming

  const merged = existing ? existing.results.slice(0) : []
  const start = (page - 1) * limit

  incoming.results.forEach((item, index) => {
    merged[start + index] = item
  })

  return { ...incoming, results: merged }
}

const isOnAuthPage = (): boolean => AUTH_ROUTES.includes(window.location.pathname)

const isUnauthorizedError = (error: unknown): boolean =>
  (ServerError.is(error) || ServerParseError.is(error)) && error.statusCode === 401

const httpLink = new HttpLink({
  uri: import.meta.env.VITE_GRAPHQL_URL ?? '/graphql',
})

const requestLink = ApolloLink.split(
  operation => variablesHaveFiles(operation.variables),
  uploadLink,
  httpLink,
)

const wsLink = new GraphQLWsLink(
  createClient({
    url: (() => {
      const httpUrl = import.meta.env.VITE_GRAPHQL_URL

      if (httpUrl) return httpUrl.replace(/^http/, 'ws')

      const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:'

      return `${proto}//${window.location.host}/graphql`
    })(),
    connectionParams: () => {
      const token = localStorage.getItem('token')

      return {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        Timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      }
    },
  }),
)

const terminatingLink = ApolloLink.split(
  ({ query }) => {
    const definition = getMainDefinition(query)

    return (
      definition.kind === 'OperationDefinition' &&
      definition.operation === 'subscription'
    )
  },
  wsLink,
  requestLink,
)

const authLink = new ApolloLink((operation, forward) => {
  const token = localStorage.getItem('token')

  operation.setContext(({ headers = {} }) => ({
    headers: {
      ...headers,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      Timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
  }))

  return forward(operation)
})

const extractErrorMessages = (message: unknown): string[] => {
  if (typeof message === 'string') {
    return [message]
  }

  if (Array.isArray(message)) {
    return message.flatMap(extractErrorMessages)
  }

  if (message !== null && typeof message === 'object') {
    return Object.values(message).flatMap(extractErrorMessages)
  }

  return []
}

const errorLink = new ErrorLink(({ error }) => {
  if (isUnauthorizedError(error) && isOnAuthPage()) {
    return
  }

  if (CombinedGraphQLErrors.is(error)) {
    const messages = error.errors.flatMap(graphQLError =>
      extractErrorMessages(graphQLError.message),
    )

    if (messages.length) {
      messages.forEach(message => toast.error(message))
    } else {
      toast.error('Something went wrong')
    }
  } else {
    toast.error(error.message)
  }
})

const client = new ApolloClient({
  link: ApolloLink.from([errorLink, authLink, terminatingLink]),
  cache: new InMemoryCache({
    typePolicies: {
      AlertSettingsQuery: {
        keyFields: [],
      },
      OrganizationQuery: {
        keyFields: [],
      },
      AlertQuery: {
        keyFields: [],
        fields: {
          getAlerts: {
            keyArgs: ['filter'],
            merge: (existing, incoming, { args }) =>
              mergePaged(
                existing,
                incoming,
                args?.paging?.page ?? 1,
                args?.paging?.limit ?? PAGE_LIMIT,
              ),
          },
        },
      },
      UserNotificationQuery: {
        keyFields: [],
        fields: {
          getUserNotifications: {
            keyArgs: ['filter'],
            merge: (existing, incoming, { args }) =>
              mergePaged(
                existing,
                incoming,
                args?.paging?.page ?? 1,
                args?.paging?.limit ?? PAGE_LIMIT,
              ),
          },
        },
      },
      UserQuery: {
        keyFields: [],
        fields: {
          getUsers: {
            keyArgs: ['filter'],
            merge: (existing, incoming, { args }) =>
              mergePaged(
                existing,
                incoming,
                args?.paging?.page ?? 1,
                args?.paging?.limit ?? PAGE_LIMIT,
              ),
          },
        },
      },
      LocationQuery: {
        keyFields: [],
        fields: {
          getLocations: {
            keyArgs: ['filter'],
            merge: (existing, incoming, { args }) =>
              mergePaged(
                existing,
                incoming,
                args?.paging?.page ?? 1,
                args?.paging?.limit ?? PAGE_LIMIT,
              ),
          },
        },
      },
      ReportQuery: {
        keyFields: [],
        fields: {
          getReportsWithPaging: {
            keyArgs: ['filter', 'sort'],
            merge: (existing, incoming, { args }) =>
              mergePaged(
                existing,
                incoming,
                args?.paging?.page ?? 1,
                args?.paging?.limit ?? PAGE_LIMIT,
              ),
          },
        },
      },
      ShiftQuery: {
        keyFields: [],
        fields: {
          getShifts: {
            keyArgs: ['filter'],
            merge: (existing, incoming, { args }) =>
              mergePaged(
                existing,
                incoming,
                args?.paging?.page ?? 1,
                args?.paging?.limit ?? PAGE_LIMIT,
              ),
          },
          getActiveShifts: {
            keyArgs: ['filter'],
            merge: (existing, incoming, { args }) =>
              mergePaged(
                existing,
                incoming,
                args?.paging?.page ?? 1,
                args?.paging?.limit ?? PAGE_LIMIT,
              ),
          },
        },
      },
    },
  }),
})

export default client
